import PropTypes from 'prop-types';
import NewsletterWidget from '@activebrands/core-web/components/Newsletter/Widget';
import NewsletterEvents from '@activebrands/core-web/components/Newsletter/Events';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import Heading from 'components/text/Heading';
import Tag from 'components/text/Tag';
import BackgroundDynamic from '@activebrands/core-web/components/BackgroundDynamic';
import getTextColorStyles from '@activebrands/core-web/utils/style-functions/get-text-color-styles';

const textBreakStyles = { minWidth: 0, overflowWrap: 'break-word' };

const Newsletter = ({
    $style = {},
    background,
    backgroundLoading = 'lazy',
    backgroundOverlay,
    contentLayout = 'default',
    heading = '',
    isPopup = false,
    newsletter = {},
    optiId,
    paragraph,
    sbEditable,
    tag = '',
    textColor,
}) => {
    const [css] = useStyletron();

    const layoutVertical = contentLayout === 'vertical';
    const layoutHorizontal = contentLayout === 'horizontal';

    let contentGridColumn = ['1 / span 12', null, null, null, '1 / span 4'];
    if (layoutVertical) contentGridColumn = '1 / span 12';
    else if (layoutHorizontal) contentGridColumn = '1 / span 4';

    let contentJustifyContent = ['flex-start', null, null, null, 'center'];
    if (layoutVertical) contentJustifyContent = 'flex-start';
    else if (layoutHorizontal) contentJustifyContent = 'center';

    let rootFlexDirection = ['column', null, null, null, 'row'];
    if (layoutVertical) rootFlexDirection = 'column';
    else if (layoutHorizontal) rootFlexDirection = 'row';

    let widgetGridColumn = ['1 / span 12', null, null, null, '6 / span 6'];
    if (layoutVertical) widgetGridColumn = '1 / span 12';
    else if (layoutHorizontal) widgetGridColumn = '6 / span 6';

    let widgetJustifyContent = ['center', null, null, null, 'flex-start'];
    if (layoutVertical) widgetJustifyContent = 'center';
    else if (layoutHorizontal) widgetJustifyContent = 'flex-start';

    return (
        <div
            data-selector="global.newsletter"
            data-opti-id={optiId}
            className={css({
                width: '100%',
                height: isPopup ? 'auto' : '100%',
                minHeight: isPopup ? undefined : '400px',
                padding: isPopup ? '36px 12px 12px' : ['12px', null, null, null, '16px'],
                position: 'relative',
                display: 'grid',
                overflow: isPopup ? 'visible' : 'hidden',
                gridTemplateColumns: 'repeat(12, 1fr)',
                flexDirection: rootFlexDirection,
                ...$style,
            })}
            {...sbEditable}
        >
            <BackgroundDynamic
                background={background}
                backgroundOverlay={backgroundOverlay}
                loading={backgroundLoading}
            />
            <div
                className={css({
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: '24px',
                    ...textBreakStyles,
                    gridColumn: contentGridColumn,
                    justifyContent: contentJustifyContent,
                    ...getTextColorStyles(textColor),
                })}
            >
                {tag?.text && (
                    <Tag
                        $style={{ marginBottom: ['16px', null, null, null, '24px'], width: 'fit-content' }}
                        data-selector="global.newsletter.eyebrow"
                        {...tag.sbEditable}
                    >
                        {tag.text}
                    </Tag>
                )}
                {heading?.text && (
                    <Heading
                        $style={{ marginBottom: '16px' }}
                        as={heading.type}
                        fontKeys={
                            isPopup
                                ? ['Primary/24_120_-2', null, null, null, 'Primary/40_100_-2']
                                : ['Primary/24_120_-2', 'Primary/32_110_-2', null, null, null, 'Primary/60_100_-3']
                        }
                        data-selector="global.newsletter.title"
                        {...heading.sbEditable}
                    >
                        {heading.text}
                    </Heading>
                )}
                {paragraph?.text && (
                    <div data-selector="global.newsletter.body" {...paragraph.sbEditable}>
                        <RichText data={paragraph.text} />
                    </div>
                )}
            </div>
            <div
                data-selector="global.newsletter.form"
                className={css({
                    display: 'flex',
                    justifyContent: widgetJustifyContent,
                    gridColumn: widgetGridColumn,
                })}
            >
                <NewsletterWidget {...newsletter} />
                <NewsletterEvents />
            </div>
        </div>
    );
};

Newsletter.propTypes = {
    $style: PropTypes.object,
    background: PropTypes.object,
    backgroundLoading: PropTypes.string,
    backgroundOverlay: PropTypes.object,
    contentLayout: PropTypes.oneOf(['default', 'horizontal', 'vertical']),
    heading: PropTypes.object,
    isPopup: PropTypes.bool,
    newsletter: PropTypes.object,
    optiId: PropTypes.string,
    paragraph: PropTypes.object,
    sbEditable: PropTypes.object,
    tag: PropTypes.object,
    textColor: PropTypes.string,
};

export default Newsletter;
